
import { useState } from "react";
import { Link } from "react-router-dom";

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [charCount, setCharCount] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus("success");
    setTimeout(() => setSubmitStatus("idle"), 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-sm z-50 border-b border-yellow-600/20">
        <nav className="px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/46562bb1bf2b4a38e1679072b6f45baa/5f0354f44941710a405b916e5b3e3bcf.jpeg" 
                alt="ROSPA WORLD PLUS ENTERPRISE Logo" 
                className="w-12 h-12 object-contain rounded-full"
              />
              <div>
                <h1 className="text-lg font-bold text-white leading-tight">
                  ROSPA WORLD PLUS <span className="text-yellow-500">ENTERPRISE</span>
                </h1>
                <p className="text-xs text-gray-400 italic">quality is our goal...</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("home")} className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">Home</button>
              <button onClick={() => scrollToSection("services")} className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">Services</button>
              <button onClick={() => scrollToSection("products")} className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">Products</button>
              <button onClick={() => scrollToSection("about")} className="text-gray-200 hover:text-yellow-500 transition-colors whitespace-nowrap cursor-pointer">About</button>
              <button onClick={() => scrollToSection("contact")} className="bg-yellow-600 text-black px-6 py-2 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">Contact Us</button>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-yellow-500 w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-700 pt-4">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection("home")} className="text-gray-200 hover:text-yellow-500 transition-colors text-left">Home</button>
                <button onClick={() => scrollToSection("services")} className="text-gray-200 hover:text-yellow-500 transition-colors text-left">Services</button>
                <button onClick={() => scrollToSection("products")} className="text-gray-200 hover:text-yellow-500 transition-colors text-left">Products</button>
                <button onClick={() => scrollToSection("about")} className="text-gray-200 hover:text-yellow-500 transition-colors text-left">About</button>
                <button onClick={() => scrollToSection("contact")} className="bg-yellow-600 text-black px-6 py-2 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer w-fit">Contact Us</button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://static.readdy.ai/image/7464a4df5b877b70c688455ce62f85a5/56c28f2d78d82207f7e17623aaac72c8.jpeg)' }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/30"></div>
        <div className="relative w-full px-6 lg:px-12 py-20">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <div className="mb-8">
                <h2 className="text-6xl lg:text-7xl font-bold text-yellow-500 mb-2" style={{ fontFamily: "'Pacifico', cursive" }}>ROSPA WORLD PLUS</h2>
                <h2 className="text-4xl lg:text-5xl font-bold text-yellow-500" style={{ fontFamily: "serif" }}>ENTERPRISE</h2>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">Quality is our goal</h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Dealers in Safety Products, Mining Consumables, Plant & Fire Equipment, and General Goods. Your trusted partner for comprehensive industrial and safety solutions.
              </p>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => scrollToSection("products")} className="bg-yellow-600 text-black px-8 py-4 rounded-md hover:bg-yellow-500 transition-all font-semibold text-lg whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl">View Products</button>
                <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white px-8 py-4 rounded-md hover:bg-green-500 transition-all font-semibold text-lg whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-whatsapp-line w-6 h-6 flex items-center justify-center"></i>
                  WhatsApp Us
                </a>
                <button onClick={() => scrollToSection("contact")} className="bg-transparent border-2 border-yellow-600 text-yellow-500 px-8 py-4 rounded-md hover:bg-yellow-600 hover:text-black transition-all font-semibold text-lg whitespace-nowrap cursor-pointer">Get a Quote</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block bg-yellow-600/10 border border-yellow-600/30 px-4 py-2 rounded-full mb-4">
              <span className="text-yellow-700 font-semibold text-sm">Our Services</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Comprehensive Business Solutions</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">We provide a complete range of products and services to meet all your business needs, from safety equipment to office supplies.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-600/30 group cursor-pointer">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-600 transition-colors">
                <i className="ri-shield-check-line text-3xl text-yellow-700 group-hover:text-black transition-colors"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Safety Products</h4>
              <p className="text-gray-600 leading-relaxed">Comprehensive range of personal protective equipment, safety gear, and workplace safety solutions to keep your team protected and compliant with industry standards.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-600/30 group cursor-pointer">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-600 transition-colors">
                <i className="ri-hammer-line text-3xl text-yellow-700 group-hover:text-black transition-colors"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Mining Consumables</h4>
              <p className="text-gray-600 leading-relaxed">High-quality mining supplies and consumables designed for durability and performance in demanding mining operations and harsh industrial environments.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-600/30 group cursor-pointer">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-600 transition-colors">
                <i className="ri-fire-line text-3xl text-yellow-700 group-hover:text-black transition-colors"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Plant &amp; Fire Equipment</h4>
              <p className="text-gray-600 leading-relaxed">Professional-grade fire safety equipment, plant machinery, and industrial tools to ensure operational safety and emergency preparedness for your facility.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-600/30 group cursor-pointer">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-600 transition-colors">
                <i className="ri-briefcase-line text-3xl text-yellow-700 group-hover:text-black transition-colors"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Office Equipment</h4>
              <p className="text-gray-600 leading-relaxed">Complete office solutions including furniture, stationery, technology equipment, and supplies to create productive and efficient workplace environments.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-600/30 group cursor-pointer">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-600 transition-colors">
                <i className="ri-store-line text-3xl text-yellow-700 group-hover:text-black transition-colors"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">General Goods</h4>
              <p className="text-gray-600 leading-relaxed">Wide selection of general merchandise and supplies for various business needs, providing convenient one-stop shopping for all your operational requirements.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-600/30 group cursor-pointer">
              <div className="w-16 h-16 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-yellow-600 transition-colors">
                <i className="ri-customer-service-line text-3xl text-yellow-700 group-hover:text-black transition-colors"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Expert Consultation</h4>
              <p className="text-gray-600 leading-relaxed">Professional guidance and consultation services to help you select the right products and solutions tailored to your specific business requirements and industry needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white" data-product-shop>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block bg-yellow-600/10 border border-yellow-600/30 px-4 py-2 rounded-full mb-4">
              <span className="text-yellow-700 font-semibold text-sm">Products & Categories</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Product Range</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Explore our comprehensive catalog of quality products designed to meet the highest industry standards. From safety equipment to office supplies, we have everything your business needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {/* Safety Equipment */}
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-200 hover:border-yellow-600/50" data-product-shop>
              <div className="relative h-64 overflow-hidden bg-white">
                <img 
                  src="https://public.readdy.ai/ai/img_res/df4ce44fb4b1c583b8bc6460b3731de9.jpg" 
                  alt="Safety Equipment" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors">Safety Equipment</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Safety Helmets</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Protective Gloves</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Reflective Vests</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Coveralls</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Face Masks</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Ear Protectors</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Safety Boots</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Goggles</span>
                  </li>
                </ul>
                <button className="w-full bg-yellow-600 text-black py-3 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">View Products</button>
              </div>
            </div>

            {/* Fire Equipment */}
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-200 hover:border-yellow-600/50" data-product-shop>
              <div className="relative h-64 overflow-hidden bg-white">
                <img 
                  src="https://public.readdy.ai/ai/img_res/33ca684c910aa8071af02c41348acb89.jpg" 
                  alt="Fire Equipment" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors">Fire Equipment</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Fire Extinguishers</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Fire Blankets</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Fire Signage</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Safety Cones</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Emergency Lights</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Smoke Detectors</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Fire Hoses</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Exit Signs</span>
                  </li>
                </ul>
                <button className="w-full bg-yellow-600 text-black py-3 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">View Products</button>
              </div>
            </div>

            {/* Mining Consumables */}
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-200 hover:border-yellow-600/50" data-product-shop>
              <div className="relative h-64 overflow-hidden bg-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=realistic%20mining%20equipment%20and%20tools%20showing%20drilling%20bits%20excavation%20tools%20mining%20helmet%20with%20lamp%20heavy%20duty%20industrial%20tools%20and%20mining%20accessories%20on%20white%20background%20professional%20mining%20consumables%20high%20quality%20product%20photography%20clear%20detailed%20view&width=600&height=600&seq=prod3real&orientation=squarish" 
                  alt="Mining Consumables" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors">Mining Consumables</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Drilling Bits</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Excavation Tools</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Safety Lamps</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Ventilation Equipment</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Mining Cables</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Explosives Accessories</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Conveyor Parts</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Protective Gear</span>
                  </li>
                </ul>
                <button className="w-full bg-yellow-600 text-black py-3 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">View Products</button>
              </div>
            </div>

            {/* Plant & Industrial Equipment */}
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-200 hover:border-yellow-600/50" data-product-shop>
              <div className="relative h-64 overflow-hidden bg-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=realistic%20industrial%20equipment%20showing%20power%20drill%20angle%20grinder%20welding%20machine%20industrial%20tools%20and%20heavy%20duty%20equipment%20on%20white%20background%20professional%20industrial%20machinery%20high%20quality%20product%20photography%20clear%20detailed%20view%20professional%20lighting&width=600&height=600&seq=prod4real&orientation=squarish" 
                  alt="Plant & Industrial Equipment" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors">Plant & Industrial Equipment</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Power Tools</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Generators</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Compressors</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Lifting Equipment</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Welding Machines</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Pumps</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Industrial Fans</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Hydraulic Tools</span>
                  </li>
                </ul>
                <button className="w-full bg-yellow-600 text-black py-3 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">View Products</button>
              </div>
            </div>

            {/* Office Equipment */}
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-200 hover:border-yellow-600/50" data-product-shop>
              <div className="relative h-64 overflow-hidden bg-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=realistic%20modern%20office%20equipment%20showing%20desktop%20computer%20printer%20scanner%20office%20desk%20supplies%20notebooks%20pens%20staplers%20and%20filing%20folders%20on%20white%20background%20professional%20office%20supplies%20high%20quality%20product%20photography%20clear%20detailed%20view%20professional%20lighting&width=600&height=600&seq=prod5real&orientation=squarish" 
                  alt="Office Equipment" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors">Office Equipment</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Computers & Laptops</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Printers & Scanners</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Office Furniture</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Stationery Supplies</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Filing Systems</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Projectors</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Telephones</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Shredders</span>
                  </li>
                </ul>
                <button className="w-full bg-yellow-600 text-black py-3 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">View Products</button>
              </div>
            </div>

            {/* Printing Supplies */}
            <div className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer border border-gray-200 hover:border-yellow-600/50" data-product-shop>
              <div className="relative h-64 overflow-hidden bg-white">
                <img 
                  src="https://readdy.ai/api/search-image?query=realistic%20printing%20supplies%20showing%20ink%20cartridges%20toner%20cartridges%20printer%20paper%20reams%20photo%20paper%20and%20printing%20accessories%20on%20white%20background%20professional%20office%20printing%20supplies%20high%20quality%20product%20photography%20clear%20detailed%20view%20professional%20lighting&width=600&height=600&seq=prod6real&orientation=squarish" 
                  alt="Printing Supplies" 
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <div className="p-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-yellow-700 transition-colors">Printing Supplies</h4>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Ink Cartridges</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Toner Cartridges</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Printing Paper</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Photo Paper</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Labels & Stickers</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Binding Supplies</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Laminating Sheets</span>
                  </li>
                  <li className="flex items-center text-gray-600">
                    <i className="ri-check-line text-yellow-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                    <span>Printer Accessories</span>
                  </li>
                </ul>
                <button className="w-full bg-yellow-600 text-black py-3 rounded-md hover:bg-yellow-500 transition-colors font-semibold whitespace-nowrap cursor-pointer">View Products</button>
              </div>
            </div>
          </div>
          <div className="mt-16 text-center">
            <p className="text-lg text-gray-600 mb-6">Can't find what you're looking for? We're constantly expanding our inventory.</p>
            <button className="bg-yellow-600 text-black px-8 py-4 rounded-md hover:bg-yellow-500 transition-all font-semibold text-lg whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg">Request a Product</button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block bg-yellow-600/10 border border-yellow-600/30 px-4 py-2 rounded-full mb-4">
                <span className="text-yellow-700 font-semibold text-sm">About Us</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Your Trusted Partner in Safety & Industrial Solutions</h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">ROSPA WORLD PLUS ENTERPRISE is a leading supplier of safety products, mining consumables, plant and fire equipment, office equipment, and general goods. We are committed to providing quality products and exceptional service to businesses across all industries.</p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">Our mission is simple: <strong>Quality is our goal</strong>. We understand that the safety and efficiency of your operations depend on reliable equipment and supplies. That's why we carefully select every product in our catalog to ensure it meets the highest standards of quality and performance.</p>
              <p className="text-lg text-gray-600 leading-relaxed">Whether you're in mining, construction, manufacturing, or office management, we have the expertise and inventory to support your needs. Our team is dedicated to building long-term relationships with our clients through reliability, competitive pricing, and outstanding customer service.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-yellow-600/50">
                <div className="w-14 h-14 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-yellow-700 text-3xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Quality Assurance</h4>
                <p className="text-gray-600 leading-relaxed">We source and supply only the highest quality products that meet international safety standards and industry requirements.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-yellow-600/50">
                <div className="w-14 h-14 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-customer-service-2-line text-yellow-700 text-3xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Customer Service</h4>
                <p className="text-gray-600 leading-relaxed">Our dedicated team provides exceptional support, ensuring your needs are met promptly and professionally at every step.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-yellow-600/50">
                <div className="w-14 h-14 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-hand-heart-line text-yellow-700 text-3xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Reliability</h4>
                <p className="text-gray-600 leading-relaxed">Count on us for consistent delivery, competitive pricing, and dependable partnerships that help your business thrive.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:border-yellow-600/50">
                <div className="w-14 h-14 bg-yellow-600/10 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-team-line text-yellow-700 text-3xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Industry Expertise</h4>
                <p className="text-gray-600 leading-relaxed">With extensive experience in safety, mining, and industrial sectors, we understand your unique challenges and requirements.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block bg-yellow-600/10 border border-yellow-600/30 px-4 py-2 rounded-full mb-4">
              <span className="text-yellow-700 font-semibold text-sm">Get In Touch</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Contact Us</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Ready to place an order or have questions? Reach out to us through any of the channels below. We're here to help!</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h4>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-yellow-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Phone Numbers</h5>
                      <a href="tel:+233552121212" className="text-gray-600 hover:text-yellow-700 transition-colors block cursor-pointer">0552121212</a>
                      <a href="tel:+233509332793" className="text-gray-600 hover:text-yellow-700 transition-colors block cursor-pointer">0509332793</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-whatsapp-line text-green-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">WhatsApp</h5>
                      <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-green-700 transition-colors block cursor-pointer">0552121212</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-yellow-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Email</h5>
                      <a href="mailto:rospaworldplus30@gmail.com" className="text-gray-600 hover:text-yellow-700 transition-colors cursor-pointer break-all">rospaworldplus30@gmail.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-yellow-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-yellow-700 text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Address</h5>
                      <p className="text-gray-600">HQR6+VPG, Accra</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h5 className="font-semibold text-gray-900 mb-4">Quick Actions</h5>
                  <div className="flex flex-wrap gap-3">
                    <a href="tel:+233552121212" className="bg-yellow-600 text-black px-6 py-3 rounded-md hover:bg-yellow-500 transition-all font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                      <i className="ri-phone-line w-5 h-5 flex items-center justify-center"></i>
                      Call Now
                    </a>
                    <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-500 transition-all font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-2">
                      <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                      WhatsApp Us
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h4>
                {submitStatus === "success" && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-md text-green-700">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input 
                      id="name" 
                      name="name"
                      required 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm" 
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input 
                      id="email" 
                      name="email"
                      required 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm" 
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input 
                      id="phone" 
                      name="phone"
                      required 
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm" 
                      placeholder="Your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">Company Name</label>
                    <input 
                      id="company" 
                      name="company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm" 
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message *</label>
                    <textarea 
                      id="message" 
                      name="message" 
                      required 
                      rows={5} 
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => {
                        setFormData({...formData, message: e.target.value});
                        setCharCount(e.target.value.length);
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-600 focus:border-transparent text-sm resize-none" 
                      placeholder="Tell us about your requirements..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{charCount}/500 characters</p>
                  </div>
                  <button type="submit" className="w-full bg-yellow-600 text-black py-4 rounded-md hover:bg-yellow-500 transition-all font-semibold text-lg whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg">
                    Get a Quote
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/46562bb1bf2b4a38e1679072b6f45baa/5f0354f44941710a405b916e5b3e3bcf.jpeg" 
                  alt="ROSPA WORLD PLUS ENTERPRISE Logo" 
                  className="w-12 h-12 object-contain rounded-full flex-shrink-0"
                />
                <div>
                  <h3 className="text-lg font-bold text-white leading-tight">ROSPA WORLD PLUS</h3>
                  <p className="text-yellow-500 font-semibold text-sm">ENTERPRISE</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 italic mb-4">quality is our goal...</p>
              <p className="text-gray-400 mb-6 leading-relaxed text-sm">Your trusted partner for safety products, mining consumables, plant and fire equipment, and general goods.</p>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/Rospaworldplusenterprise" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors cursor-pointer" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a href="https://www.instagram.com/_Rospaworld.plusenterprise23" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors cursor-pointer" aria-label="Instagram">
                  <i className="ri-instagram-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a href="https://wa.me/233509332793" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer" aria-label="WhatsApp">
                  <i className="ri-whatsapp-fill text-xl w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection("home")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Home</button></li>
                <li><button onClick={() => scrollToSection("about")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">About Us</button></li>
                <li><button onClick={() => scrollToSection("products")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Products</button></li>
                <li><button onClick={() => scrollToSection("services")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Services</button></li>
                <li><button onClick={() => scrollToSection("contact")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Contact</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Product Categories</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection("products")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Safety Equipment</button></li>
                <li><button onClick={() => scrollToSection("products")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Fire Equipment</button></li>
                <li><button onClick={() => scrollToSection("products")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Mining Consumables</button></li>
                <li><button onClick={() => scrollToSection("products")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Plant Equipment</button></li>
                <li><button onClick={() => scrollToSection("products")} className="hover:text-yellow-500 transition-colors cursor-pointer text-left">Office Supplies</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-lg mb-4">Contact Info</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-phone-line text-yellow-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <a href="tel:+233552121212" className="hover:text-yellow-500 transition-colors cursor-pointer block">0552121212</a>
                    <a href="tel:+233509332793" className="hover:text-yellow-500 transition-colors cursor-pointer block">0509332793</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-whatsapp-line text-green-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <a href="https://wa.me/233552121212" target="_blank" rel="noopener noreferrer" className="hover:text-green-500 transition-colors cursor-pointer">0552121212</a>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line text-yellow-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <a href="mailto:rospaworldplus30@gmail.com" className="hover:text-yellow-500 transition-colors cursor-pointer break-all">rospaworldplus30@gmail.com</a>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-yellow-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>HQR6+VPG, Accra</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center md:text-left">© 2025 ROSPA WORLD PLUS ENTERPRISE. All rights reserved.</p>
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-yellow-500 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
